import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Mic, Users, Presentation, Award } from 'lucide-react';

export const SpeakingSection = ({ data }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const iconMap = {
    'Podcast Host': Mic,
    'Guest Appearance': Users,
    'Award Winner': Award,
    'Speaker': Presentation,
  };

  const imageMap = {
    'Podcast Host': '/images/podcast-founders-mindset.png',
    'Guest Appearance': '/images/speaking-stage.png',
    'Award Winner': '/images/10k-women-group-photo.webp',
    'Speaker': '/images/conference-10kwomen.png',
  };

  // Title-specific images override type-based images
  const titleImageMap = {
    'Happy Lab Podcast': '/images/happylab-office-team.png',
    'Founder Psychology Series': '/images/podcast-founders-mindset.png',
  };

  return (
    <section className="py-24 md:py-32 bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            {data.title}
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            {data.description}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {data.events.map((event, index) => {
            const Icon = iconMap[event.type] || Mic;
            const image = titleImageMap[event.title] || imageMap[event.type];
            const hasLink = event.link && event.link !== '#';
            
            const CardContent = () => (
              <>
                {/* Image */}
                {image && (
                  <div className="aspect-video overflow-hidden">
                    <img 
                      src={image} 
                      alt={event.title} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                )}
                
                {/* Content */}
                <div className="p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div className="flex-1">
                      <span className="text-sm font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wide">
                        {event.type}
                      </span>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mt-2 mb-2">{event.title}</h3>
                      <p className="text-gray-600 dark:text-gray-400">{event.description}</p>
                    </div>
                  </div>
                </div>
              </>
            );
            
            return hasLink ? (
              <motion.a
                key={index}
                href={event.link}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group bg-white dark:bg-gray-800 rounded-3xl overflow-hidden border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-all duration-300 block cursor-pointer">
                <CardContent />
              </motion.a>
            ) : (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group bg-white dark:bg-gray-800 rounded-3xl overflow-hidden border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-all duration-300">
                <CardContent />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};